import { useTranslation } from 'react-i18next'
import { GOAL_OPTIONS, LEVEL_OPTIONS } from './planHelpers'

export function PlanGeneratorForm({ options, onChange, onSubmit, isGenerating = false, error = false }) {
  const { t } = useTranslation()
  const set = (field, value) => onChange({ ...options, [field]: value })

  const toggleGoal = (goal) => {
    const goals = options.fitness_goals.includes(goal)
      ? options.fitness_goals.filter((g) => g !== goal)
      : [...options.fitness_goals, goal]
    set('fitness_goals', goals)
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    onSubmit()
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <fieldset>
        <legend className="block text-sm font-medium text-gray-700 mb-1">{t('aiPlan.form.goals')}</legend>
        <div className="flex flex-wrap gap-2">
          {GOAL_OPTIONS.map((goal) => (
            <label key={goal} className="flex items-center gap-1 text-sm">
              <input
                type="checkbox"
                checked={options.fitness_goals.includes(goal)}
                onChange={() => toggleGoal(goal)}
              />
              {t(`aiPlan.goals.${goal}`)}
            </label>
          ))}
        </div>
      </fieldset>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <label className="block text-sm font-medium text-gray-700">
          {t('aiPlan.form.level')}
          <select
            value={options.fitness_level}
            onChange={(e) => set('fitness_level', e.target.value)}
            className="mt-1 w-full"
          >
            {LEVEL_OPTIONS.map((level) => (
              <option key={level} value={level}>{t(`aiPlan.levels.${level}`)}</option>
            ))}
          </select>
        </label>
        <label className="block text-sm font-medium text-gray-700">
          {t('aiPlan.form.daysPerWeek')}
          <input
            type="number" min={1} max={7}
            value={options.days_per_week}
            onChange={(e) => set('days_per_week', e.target.value)}
            className="mt-1 w-full"
          />
        </label>
        <label className="block text-sm font-medium text-gray-700">
          {t('aiPlan.form.sessionMinutes')}
          <input
            type="number" min={15} max={120} step={5}
            value={options.session_duration_minutes}
            onChange={(e) => set('session_duration_minutes', e.target.value)}
            className="mt-1 w-full"
          />
        </label>
      </div>

      <label className="block text-sm font-medium text-gray-700">
        {t('aiPlan.form.injuries')}
        <textarea
          rows={2} maxLength={500}
          value={options.injuries_limitations}
          onChange={(e) => set('injuries_limitations', e.target.value)}
          className="mt-1 w-full"
        />
      </label>

      <div className="rounded-md border border-gray-200 p-3">
        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            checked={options.use_analysis}
            onChange={(e) => set('use_analysis', e.target.checked)}
            className="mt-1"
          />
          <span>
            <span className="font-medium text-gray-900">{t('aiPlan.form.useAnalysis')}</span>
            <span className="block text-gray-500">
              {options.use_analysis ? t('aiPlan.form.useAnalysisHint') : t('aiPlan.form.preferencesOnlyHint')}
            </span>
          </span>
        </label>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <label className="block text-sm font-medium text-gray-700">
          {t('aiPlan.form.blockWeeks')}
          <input
            type="number" min={1} max={12}
            value={options.block_weeks}
            onChange={(e) => set('block_weeks', e.target.value)}
            className="mt-1 w-full"
          />
          <span className="block text-xs text-gray-500 mt-1">{t('aiPlan.form.blockWeeksHint')}</span>
        </label>
        <label className="block text-sm font-medium text-gray-700">
          {t('aiPlan.form.startYear')}
          <input
            type="number" min={2020} max={2100}
            value={options.start_year}
            onChange={(e) => set('start_year', e.target.value)}
            className="mt-1 w-full"
          />
        </label>
        <label className="block text-sm font-medium text-gray-700">
          {t('aiPlan.form.startWeek')}
          <input
            type="number" min={1} max={53}
            value={options.start_week}
            onChange={(e) => set('start_week', e.target.value)}
            className="mt-1 w-full"
          />
        </label>
      </div>

      {error && (
        <p role="alert" className="text-sm text-red-600">{t('aiPlan.errors.generate')}</p>
      )}

      <div className="flex justify-end">
        <button
          type="submit"
          className="btn-primary"
          disabled={isGenerating || options.fitness_goals.length === 0}
        >
          {isGenerating ? t('aiPlan.form.generating') : t('aiPlan.form.generate')}
        </button>
      </div>
    </form>
  )
}
